import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Mail, KeyRound, CheckCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password, 4: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const it = language === 'it';

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim() });
      // In development, the code is returned. In production, it would be sent via email
      if (res.data.code) {
        setResetCode(res.data.code);
      }
      setStep(2);
      toast.success(it ? 'Codice di reset generato!' : 'Reset code generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error(it ? 'Inserisci il codice a 6 cifre' : 'Enter the 6-digit code');
      return;
    }
    setStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(it ? 'La password deve avere almeno 6 caratteri' : 'Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(it ? 'Le password non corrispondono' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email: email.trim(),
        code,
        new_password: newPassword
      });
      setStep(4);
      toast.success(it ? 'Password aggiornata!' : 'Password updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-lg border border-stone-200 p-8">
          {/* Header */}
          <Link to="/login" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 text-sm mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            {it ? 'Torna al login' : 'Back to login'}
          </Link>

          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleRequestCode}>
              <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-5">
                <Mail className="h-7 w-7 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
                {it ? 'Recupera password' : 'Reset password'}
              </h1>
              <p className="text-stone-500 text-sm mb-6">
                {it ? 'Inserisci la tua email e riceverai un codice per reimpostare la password.' : 'Enter your email and you will receive a code to reset your password.'}
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{it ? 'Email' : 'Email'}</Label>
                  <Input data-testid="forgot-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="email@esempio.it" className="h-12 rounded-xl" required />
                </div>
                <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                  className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                  {loading ? '...' : (it ? 'Invia codice' : 'Send code')}
                </Button>
              </div>
            </form>
          )}

          {/* Step 2: Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode}>
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-5">
                <KeyRound className="h-7 w-7 text-blue-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
                {it ? 'Inserisci il codice' : 'Enter the code'}
              </h1>
              <p className="text-stone-500 text-sm mb-4">
                {it ? 'Inserisci il codice a 6 cifre per procedere.' : 'Enter the 6-digit code to proceed.'}
              </p>

              {/* Show code in dev mode */}
              {resetCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-amber-800 font-medium mb-1">
                    {it ? 'Il tuo codice di reset:' : 'Your reset code:'}
                  </p>
                  <p className="text-3xl font-mono font-bold text-amber-900 tracking-[0.3em] text-center" data-testid="reset-code-display">
                    {resetCode}
                  </p>
                  <p className="text-xs text-amber-600 mt-2 text-center">
                    {it ? 'Valido per 30 minuti' : 'Valid for 30 minutes'}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{it ? 'Codice a 6 cifre' : '6-digit code'}</Label>
                  <Input data-testid="reset-code-input" type="text" inputMode="numeric" maxLength={6}
                    value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000" className="h-14 rounded-xl text-center text-2xl font-mono tracking-[0.5em]" required />
                </div>
                <Button type="submit" data-testid="verify-code-btn"
                  className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                  {it ? 'Verifica codice' : 'Verify code'}
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
                <Lock className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
                {it ? 'Nuova password' : 'New password'}
              </h1>
              <p className="text-stone-500 text-sm mb-6">
                {it ? 'Scegli una nuova password sicura.' : 'Choose a new secure password.'}
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{it ? 'Nuova password' : 'New password'}</Label>
                  <Input data-testid="new-password-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder={it ? 'Minimo 6 caratteri' : 'Minimum 6 characters'} className="h-12 rounded-xl" required />
                </div>
                <div className="space-y-2">
                  <Label>{it ? 'Conferma password' : 'Confirm password'}</Label>
                  <Input data-testid="confirm-password-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={it ? 'Ripeti la password' : 'Repeat password'} className="h-12 rounded-xl" required />
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-password-btn"
                  className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                  {loading ? '...' : (it ? 'Aggiorna password' : 'Update password')}
                </Button>
              </div>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">
                {it ? 'Password aggiornata!' : 'Password updated!'}
              </h1>
              <p className="text-stone-500 text-sm mb-6">
                {it ? 'La tua password è stata aggiornata con successo. Puoi ora accedere con la nuova password.' : 'Your password has been updated. You can now login with your new password.'}
              </p>
              <Button onClick={() => navigate('/login')} data-testid="go-to-login-btn"
                className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold">
                {it ? 'Vai al login' : 'Go to login'}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
